import React, {useEffect, useRef, useState} from 'react';
import {Button} from 'antd';
import Header from '../../components/molecules/Header/component';
import Footer from '../../components/molecules/Footer/component';
import Seo from '../../components/atoms/Seo/component';
import {openSignup} from '../../utils/helpers';
import productService from '../../services/productService';
import {trackEvent} from '../../analytics/posthog';
import {useTrackOnView} from '../../analytics/useTrackOnView';
import './style.css';

const openPartnerSignup = () => {
    trackEvent('partner_guide_start_as_partner_clicked', {
        selected_role: 'partner',
        signup_source: 'partner_guide_start_as_partner'
    });
    openSignup('partner', 'partner_guide_start_as_partner');
};

type ProductPrice = {
    unit_amount?: number;
    currency?: string;
    recurring?: {
        interval?: string;
        interval_count?: number;
    };
};

type Product = {
    name: string;
    default_price?: ProductPrice;
    images?: string[];
    description?: string;
};

const benefits = [
    {
        image: '/assets/images/partner-guide/recurring-revenue.png',
        title: 'Recurring revenue',
        text: 'Earn 40% of subscription payments for as long as your referrals stay active.'
    },
    {
        image: '/assets/images/partner-guide/share-advertiser-spend.png',
        title: 'Share advertiser spend',
        text: 'Earn 10% of campaign spend from advertisers you refer as they launch and grow campaigns.'
    },
    {
        image: '/assets/images/partner-guide/built-in-tools.png',
        title: 'Built-in tools',
        text: 'Referral links, tracking, dashboards, onboarding tools and wallet withdrawals.'
    }
];

const promoterAccessItems = [
    'Browse campaigns',
    'Select campaigns to promote',
    'Generate unique affiliate links',
    'Earn per click',
    'Track everything in your dashboard',
    'Manage earnings in your wallet'
];

const dashboardItems = [
    'Who joined through your link',
    'Which referrals are active subscribers',
    'Subscription commission generated (40%)',
    'Advertiser spend commission (10%) from advertisers you referred',
    'Wallet balance and transaction history'
];

const steps = [
    {
        title: 'Subscribe as a Partner',
        text: 'Become a partner and help expand the network while earning referral income.'
    },
    {
        title: 'Promoters share & earn',
        text: 'Promoters pick campaigns, share links and get paid per click.'
    },
    {
        title: 'Partners recruit & reap',
        text: 'Bring new users on board and earn from their subscriptions and advertiser spend.'
    },
    {
        title: 'Advertisers fund campaigns',
        text: 'Choose your preferred plan, set up cost-per-campaign, budgets and scale.'
    }
];

const partnerFaqs = [
    {
        question: 'Do Partners also earn per click like Promoters?',
        answer: 'Yes. Partners have full Promoter functionality and can promote campaigns to earn per click.'
    },
    {
        question: 'Do I earn 10% from every advertiser on Promotify?',
        answer: 'No. You earn advertiser spend commission only from advertisers you personally refer.'
    },
    {
        question: 'Is it lifetime income?',
        answer: 'It is recurring while your referred users stay subscribed and your referred advertisers continue spending.'
    }
];

const getPlanSortValue = (product: Product) => {
    const price = product.default_price;
    const interval = price?.recurring?.interval;
    const intervalCount = price?.recurring?.interval_count || 1;

    if (interval === 'month') return intervalCount;
    if (interval === 'year') return 12 * intervalCount;

    return Number.MAX_SAFE_INTEGER;
};

const formatPlanFromProduct = (product: Product) => {
    const price = product.default_price;
    const amount = typeof price?.unit_amount === 'number' ? price.unit_amount / 100 : 0;
    const currency = price?.currency?.toUpperCase?.() || 'USD';
    const interval = price?.recurring?.interval;
    const intervalCount = price?.recurring?.interval_count || 1;
    const cadence = interval
        ? intervalCount === 1
            ? `Per ${interval}`
            : `Every ${intervalCount} ${interval}s`
        : '';
    return {
        name: product.name.replace(' - Partner', ''),
        price: `$${amount.toFixed(2)}`,
        cadence,
        currency,
        image: product.images?.[0],
        description: product.description
    };
};

const planHighlights = [
    'Partnering Promoter plan',
    '40% commission on referrals',
    '10% commission on campaign spend',
    'Priority support and early feature access'
];

const partnerBenefits = [
    'Become a 40% of subscription payments recurring commission',
    'Access to 10% of campaign spend from advertisers you welcome',
    'Invite links, partner dashboard, and reporting tools',
    'Transparent wallet payouts'
];

const scrollToPlan = () => {
    trackEvent('partner_guide_see_pricing_clicked', {
        selected_role: 'partner'
    });
    document.getElementById('partner-plan')?.scrollIntoView({behavior: 'smooth'});
};

const PartnerGuidePage: React.FC = () => {
    const [partnerProducts, setPartnerProducts] = useState<Product[]>([]);
    const [isLoadingProducts, setIsLoadingProducts] = useState(true);
    const pricingSectionRef = useRef<HTMLElement>(null);
    const planOptionsRef = useRef<HTMLDivElement>(null);

    useTrackOnView(pricingSectionRef, 'partner_guide_pricing_section_viewed', {
        selected_role: 'partner',
        section_name: 'partner_pricing'
    });
    useTrackOnView(planOptionsRef, 'partner_guide_plan_viewed', {
        selected_role: 'partner',
        plan_name: 'Partner'
    });

    useEffect(() => {
        window.scrollTo({top: 0, left: 0, behavior: 'auto'});
        trackEvent('partner_guide_page_viewed', {
            selected_role: 'partner'
        });
    }, []);

    useEffect(() => {
        productService.getProducts('partner', {skipSubscriptionCheck: true})
            .then(products => {
                setPartnerProducts(Array.isArray(products) ? products : []);
            })
            .finally(() => setIsLoadingProducts(false));
    }, []);

    const partnerPlans = [...partnerProducts]
        .sort((a, b) => getPlanSortValue(a) - getPlanSortValue(b))
        .map(formatPlanFromProduct);

    return (
        <div className="promoterGuide partnerGuide">
            <Seo
                title="Partner Guide | Promotify"
                description="Learn how partners use Promotify to invite promoters and advertisers, earn recurring commissions, and track referrals."
                canonicalPath="/partner-guide"
            />
            <Header/>

            <main>
                <section className="promoterGuideHero">
                    <div className="promoterGuideDecor decorRing"/>
                    <div className="promoterGuideDecor decorPlus">+</div>
                    <div className="promoterGuideDecor decorDot"/>
                    <div className="promoterGuideContainer promoterGuideHeroGrid">
                        <div className="promoterGuideHeroCopy">
                            <span className="promoterGuideEyebrow">Partner guide</span>
                            <h1>
                                Earn recurring income with PromotifyAI
                            </h1>
                            <p>
                                Refer businesses and promoters to PromotifyAI and earn 40% recurring commission on every
                                subscription, plus 10% of advertiser campaign spend you bring to the platform.
                            </p>
                            <div className="promoterGuideActions">
                                <Button type="primary" onClick={openPartnerSignup}>Start as Partner</Button>
                                <Button onClick={scrollToPlan}>See pricing</Button>
                            </div>
                            <small>Build your partner network, track your earnings, and withdraw anytime.</small>
                        </div>

                        <div className="promoterGuideHeroImage">
                            <div className="partnerGuideSnapshot">
                                <strong>Earn While They Advertise!</strong>
                                <ul>
                                    <li>40% of subscription revenue</li>
                                    <li>10% of ad spend from advertisers you refer</li>
                                    <li>Fully automatic tracking & payouts</li>
                                </ul>
                                <strong className="partnerGuideNumbersTitle">Lets talk REAL NUMBERS!</strong>
                                <ul>
                                    <li>$500 spent in ads - <b>$50 to you</b></li>
                                    <li>$5,000 spent in ads - <b>$500 to you</b></li>
                                </ul>
                            </div>
                            <img
                                src="/assets/images/partner-guide/partner-main.png"
                                alt="Partner referral commission illustration"
                            />
                        </div>
                    </div>
                </section>

                <section className="promoterGuideSection promoterGuideIntro">
                    <div className="promoterGuideContainer promoterGuideNarrow">
                        <span className="promoterGuideEyebrow">Why PromotifyAI</span>
                        <h2>Most affiliate programs pay once. Partners earn while the network grows.</h2>
                        <p>
                            Invite advertisers and promoters, help them activate, and keep earning as their subscriptions
                            and campaign spend continue inside Promotify.
                        </p>
                    </div>

                    <div className="promoterGuideContainer promoterGuideCards">
                        {benefits.map((card, index) => (
                            <article className={`promoterGuideCard card${index + 1}`} key={card.title}>
                                <img src={card.image} alt="" aria-hidden="true"/>
                                <h3>{card.title}</h3>
                                <p>{card.text}</p>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="promoterGuideSection promoterGuideStepsSection">
                    <div className="promoterGuideContainer promoterGuideNarrow">
                        <span className="promoterGuideEyebrow">How it works</span>
                        <h2>4 simple steps</h2>
                    </div>
                    <div className="promoterGuideContainer promoterGuideSteps">
                        {steps.map((step, index) => (
                            <article className="promoterGuideStep" key={step.title}>
                                <div className="promoterGuideStepTop">
                                    <span className="promoterGuideNumber">{index + 1}</span>
                                    {index < steps.length - 1 && (
                                        <span className="promoterGuideStepArrow" aria-hidden="true"/>
                                    )}
                                </div>
                                <div>
                                    <h3>{step.title}</h3>
                                    <p>{step.text}</p>
                                </div>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="promoterGuideSection partnerGuideAccessSection">
                    <div className="promoterGuideContainer partnerGuideAccessGrid">
                        <div>
                            <span className="promoterGuideEyebrow">Promoter benefits included</span>
                            <h2>Everything a Promoter gets, plus partner-only income.</h2>
                            <p>
                                Partners get full Promoter access first, so you can promote campaigns yourself while
                                building a referral network that earns recurring commission.
                            </p>
                        </div>
                        <div className="partnerGuideAccessCard">
                            <ul>
                                {promoterAccessItems.map(item => <li key={item}>{item}</li>)}
                            </ul>
                        </div>
                    </div>
                </section>

                <section className="promoterGuideSection partnerGuideEarningsSection">
                    <div className="promoterGuideContainer partnerGuideEarningsGrid">
                        <article className="partnerGuideEarningCard">
                            <span>1</span>
                            <h2>Subscription commission</h2>
                            <p>
                                Earn <strong>40% of every subscription</strong> from anyone you refer as long as they
                                remain an active subscriber.
                            </p>
                        </article>
                        <article className="partnerGuideEarningCard">
                            <span>2</span>
                            <h2>Advertiser spend commission</h2>
                            <p>
                                When you refer an Advertiser, you also earn <strong>10% of their monthly click spend</strong>.
                            </p>
                        </article>
                        <div className="partnerGuideExample">
                            <strong>Example</strong>
                            <p>
                                A referred advertiser spends <b>$1,500/month</b> on clicks, so you earn
                                <b> $150/month</b> from spend plus subscription commission.
                            </p>
                        </div>
                    </div>
                </section>

                <section className="promoterGuideSection promoterGuideDashboardSection">
                    <div className="promoterGuideContainer promoterGuideDashboard">
                        <div>
                            <span className="promoterGuideEyebrow">What you can track</span>
                            <h2>Your Partner dashboard shows the network behind your income.</h2>
                            <ul className="promoterGuideCheckList">
                                {dashboardItems.map(item => <li key={item}>{item}</li>)}
                            </ul>
                        </div>
                        <div className="promoterGuideMockup" aria-hidden="true">
                            <div className="promoterGuideMockupStats">
                                <div>
                                    <span>Referral Revenue</span>
                                    <strong>$1,840</strong>
                                </div>
                                <div>
                                    <span>Active Referrals</span>
                                    <strong>28</strong>
                                </div>
                                <div>
                                    <span>Ad Spend Share</span>
                                    <strong>$620</strong>
                                </div>
                            </div>
                            <div className="promoterGuideChart">
                                <span style={{height: '42%'}}/>
                                <span style={{height: '56%'}}/>
                                <span style={{height: '48%'}}/>
                                <span style={{height: '68%'}}/>
                                <span style={{height: '74%'}}/>
                                <span style={{height: '88%'}}/>
                            </div>
                            <div className="promoterGuideRows">
                                <div><span>Local advertiser</span><span>Active</span></div>
                                <div><span>Creator referral</span><span>Active</span></div>
                                <div><span>Campaign spend</span><span>Tracked</span></div>
                            </div>
                        </div>
                    </div>
                </section>

                <section ref={pricingSectionRef} className="promoterGuideSection promoterGuidePlanSection" id="partner-plan">
                    <div className="promoterGuideContainer promoterGuideNarrow">
                        <span className="promoterGuideEyebrow">Pricing package</span>
                        <h2>Partner Plan</h2>
                    </div>

                    <div className="promoterGuidePlan">
                        <div className="promoterGuidePlanTop">
                            <div>
                                <h3>Partner earnings explained</h3>
                                <ul className="promoterGuidePlanFeatures">
                                    {partnerBenefits.map(item => <li key={item}>{item}</li>)}
                                </ul>
                            </div>
                            <div>
                                <div ref={planOptionsRef} className="promoterGuidePlanOptions">
                                    {isLoadingProducts && (
                                        <div className="promoterGuidePlanStatus">Loading partner plans...</div>
                                    )}
                                    {!isLoadingProducts && partnerPlans.length === 0 && (
                                        <div className="promoterGuidePlanStatus">Partner plans are unavailable right now.</div>
                                    )}
                                    {!isLoadingProducts && partnerPlans.map(plan => (
                                        <article className="promoterGuidePlanOption" key={plan.name}>
                                            <img
                                                className="promoterGuidePlanImage"
                                                src={plan.image || '/assets/images/promotify-transparent.png'}
                                                alt={plan.name}
                                            />
                                            <div>
                                                <h3>{plan.name}</h3>
                                                {plan.description && (
                                                    <p className="promoterGuidePlanDescription">
                                                        <em>{plan.description}</em>
                                                    </p>
                                                )}
                                            </div>
                                            <p className="promoterGuidePlanPrice">
                                                {plan.price}<span> {plan.currency}</span>
                                            </p>
                                        </article>
                                    ))}
                                    {!isLoadingProducts && partnerPlans.length === 0 && (
                                        <article className="promoterGuidePlanOption">
                                            <img
                                                className="promoterGuidePlanImage"
                                                src="/assets/images/promotify-transparent.png"
                                                alt="Promotify partner plan"
                                            />
                                            <div>
                                                <h3>Partner</h3>
                                                <p className="promoterGuidePlanDescription">
                                                    <em>{planHighlights.join(' / ')}</em>
                                                </p>
                                            </div>
                                            <p className="promoterGuidePlanPrice">$175.00<span> USD</span></p>
                                        </article>
                                    )}
                                </div>
                            </div>
                            <div className="promoterGuidePlanCta">
                                <Button type="primary" onClick={openPartnerSignup}>
                                    Sign up
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="promoterGuideFaqSection">
                    <div className="promoterGuideContainer">
                        <div className="promoterGuideFaqCard">
                            <div>
                                <h2>Frequently asked questions</h2>
                                {partnerFaqs.map(faq => (
                                    <details
                                        key={faq.question}
                                        onToggle={(event) => {
                                            if ((event.currentTarget as HTMLDetailsElement).open) {
                                                trackEvent('partner_guide_faq_opened', {
                                                    selected_role: 'partner',
                                                    faq_question: faq.question
                                                });
                                            }
                                        }}
                                    >
                                        <summary>{faq.question}</summary>
                                        <p>{faq.answer}</p>
                                    </details>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="promoterGuideAnimationSection">
                    <video
                        src="/assets/videos/logo-animation-dark.mp4"
                        autoPlay
                        muted
                        loop
                        playsInline
                        aria-label="Promotify animated logo"
                    />
                </section>
            </main>

            <Footer/>
        </div>
    );
};

export default PartnerGuidePage;
